/**
 * Row-action builders for the Product Information table view.
 *
 * Layout follows the BM-9592 order, split across the pinned actions column. Edit /
 * Duplicate / Completion Indicator are visible icons; Add to Group / Archive / History
 * continue the same order inside the 3-dot "more" menu, which also carries the
 * contextual Unlink and Delete/Trash. History is unconditional for a non-supplier, so
 * the 3-dot affordance is present on every row and never opens empty. Every action
 * reuses the product card's handlers and permission gating, and opens the same modals
 * (no new modals).
 *
 * These builders return framework-agnostic descriptors; the table page renders them
 * inside BombiixAGGrid's `renderRowActionExtras` (RowActionButtons + RowMoreMenu).
 */

import { ProductItem, TableRow, TFunc } from './types';

type ProductHandler = (product?: ProductItem) => void;

/** A visible (outside) icon action. */
export interface RowActionConfig {
  icon: string;
  title: string;
  className?: string;
  /** Greys out this one action, leaving the row's others usable. */
  disabled?: boolean;
  onClick: (row: TableRow) => void;
}

/** A 3-dot menu entry (or a divider). */
export interface MoreAction {
  label?: string;
  icon?: string;
  danger?: boolean;
  divider?: boolean;
  onClick?: () => void;
}

export interface RowActionHandlers {
  /** Edit a row inline. The clicked row is passed so a cluster child is identified by its path key. */
  onEdit: (product?: ProductItem, row?: TableRow) => void;
  onArchive: ProductHandler;
  onDuplicate: ProductHandler;
  onCompletion: ProductHandler;
  onGroup: ProductHandler;
  onUnlink: ProductHandler;
  onHistory: ProductHandler;
  onTrash: ProductHandler;
  onDelete: ProductHandler;
}

interface RowActionAccess {
  addEdit: boolean;
  delete: boolean;
}

interface VisibleActionsCtx {
  t: TFunc;
  handlers: RowActionHandlers;
  access: RowActionAccess;
  activeList: string;
  /** A supplier login sees no visible row icons, mirroring the product card. */
  isSupplier?: boolean;
}

interface MoreActionsCtx {
  t: TFunc;
  handlers: RowActionHandlers;
  access: RowActionAccess;
  activeList: string;
  /** Whether Add to Group is enabled for this company (group add/edit right + company toggle). */
  groupAllowed: boolean;
  /** Whether archive/unarchive is permitted for the current list and role. */
  archiveAllowed: boolean;
  trashAllowed: boolean;
  /** A supplier login gets no row menu at all, mirroring the product card. */
  isSupplier?: boolean;
}

/** Build the inline Save / Cancel actions shown on the row currently being added/edited. */
export const buildInlineRowActions = ({ t, onSave, onCancel }: { t: TFunc; onSave: (row: TableRow) => void; onCancel: () => void }): RowActionConfig[] => [
  {
    icon: 'bombiix-icon-save',
    title: t('COMMON_LABEL.SAVE'),
    className: 'bombiix-icon-container--green-hover',
    onClick: (row) => onSave(row),
  },
  {
    icon: 'bombiix-icon-close',
    title: t('COMMON_LABEL.CANCEL'),
    className: 'bombiix-icon-container--red',
    onClick: () => onCancel(),
  },
];

/**
 * The single action a bulk-edit row carries: drop this product from the batch.
 *
 * Bulk rows deliberately have no per-row Save/Cancel — the floating bar commits the whole batch,
 * and a second commit path invites half-saved batches. Removing one row is a different operation:
 * it changes what the batch contains rather than writing anything, so it belongs on the row.
 *
 * It also fills a gap: row checkboxes are locked while bulk edit is open, so without this there is
 * no way to drop one product without cancelling the entire batch.
 * @param options translator and the remove handler
 * @returns the row-action config for a bulk-edit row
 */
export const buildBulkRowActions = ({ t, onRemove }: { t: TFunc; onRemove: (row: TableRow) => void }): RowActionConfig[] => [
  {
    icon: 'bombiix-icon-close',
    title: t('product-information:REMOVE_FROM_SELECTION'),
    className: 'bombiix-icon-container--red',
    onClick: (row) => onRemove(row),
  },
];

/** Build the visible (outside) row actions — the first three of the BM-9592 order: Edit, Duplicate, Completion Indicator. */
export const buildVisibleRowActions = ({ t, handlers, access, activeList, isSupplier }: VisibleActionsCtx): RowActionConfig[] => {
  const actions: RowActionConfig[] = [];

  /* A supplier login gets no outside icons — every card action button is supplier-hidden. */
  if (isSupplier) return actions;

  if (access.addEdit && !['archived', 'bin'].includes(activeList)) {
    actions.push({
      icon: 'bombiix-icon-edit',
      title: t('ADD_PO_TEMPLATE_PROVIDER.EDITS'),
      className: 'bombiix-icon-container--blue',
      onClick: (row) => handlers.onEdit(row.__product, row),
    });
  }

  if (access.addEdit && ['open', 'reorder'].includes(activeList)) {
    actions.push({
      icon: 'bombiix-icon-duplicate',
      title: t('FILTER.DUPLICATE_DATA_PACK'),
      className: 'bombiix-icon-container--blue',
      onClick: (row) => handlers.onDuplicate(row.__product),
    });
  }

  actions.push({
    icon: 'bombiix-icon-progress-indicator',
    title: t('FILTER.COMPLETION_INDECATORS'),
    className: 'bombiix-icon-container--blue',
    onClick: (row) => handlers.onCompletion(row.__product),
  });

  return actions;
};

/** Build the "more" (3-dot) menu — the rest of the BM-9592 order (Add to Group, Archive, History) plus the contextual Unlink and Delete/Trash. */
export const buildMoreRowActions =
  ({ t, handlers, access, activeList, groupAllowed, archiveAllowed, trashAllowed, isSupplier }: MoreActionsCtx) =>
  (row: TableRow): MoreAction[] => {
    const product = row.__product;
    const actions: MoreAction[] = [];

    /*
     * A supplier login gets NO row actions at all — mirroring the product card, where every
     * action (incl. History) is hidden for suppliers. Returning empty makes RowActions render
     * no 3-dot menu, so the row shows nothing actionable.
     */
    if (isSupplier) return actions;

    if (groupAllowed && access.addEdit && activeList === 'open') {
      actions.push({
        label: t('FILTER.ADD_TO_GRUP'),
        icon: 'bombiix-icon-product-group',
        onClick: () => handlers.onGroup(product),
      });
    }

    if (archiveAllowed && access.addEdit && activeList !== 'bin') {
      actions.push({
        label: activeList === 'open' ? t('FILTER.ARCHIVE') : t('FILTER.UNARCHIVE'),
        icon: 'bombiix-icon-archive',
        onClick: () => handlers.onArchive(product),
      });
    }

    actions.push({
      label: t('TOP_NAVBAR.HISTORY'),
      icon: 'bombiix-icon-duration-counter',
      onClick: () => handlers.onHistory(product),
    });

    /*
     * Unlink is offered only on an expanded child (reorder) row — a member of a cluster — so
     * it can be detached from its parent. The card view keys this off parentDevOrderId; here the
     * grid marks every reorder child with __isReorder, which is the reliable signal on this row.
     */
    if (access.addEdit && activeList === 'open' && row.__isReorder) {
      actions.push({
        label: t('FILTER.UNLIKE'),
        icon: 'bombiix-icon-link',
        onClick: () => handlers.onUnlink(product),
      });
    }

    if (trashAllowed && access.delete && activeList === 'archived') {
      actions.push({ divider: true });
      actions.push({
        label: t('FILTER.TRASH'),
        icon: 'bombiix-icon-delete',
        danger: true,
        onClick: () => handlers.onTrash(product),
      });
    }

    if (access.delete && activeList === 'bin') {
      actions.push({ divider: true });
      actions.push({
        label: t('TAKING_DATES.DELETES'),
        icon: 'bombiix-icon-delete',
        danger: true,
        onClick: () => handlers.onDelete(product),
      });
    }

    return actions;
  };

interface DraftRowActionsCtx {
  t: TFunc;
  /** False at the multi-add cap: the row "+" stays visible but disabled. */
  canAdd: boolean;
  onSave: (row: TableRow) => void;
  onAddBelow: (row: TableRow) => void;
  onRemove: (row: TableRow) => void;
}

/**
 * Actions on a multi-add draft row (BM-9594), laid out as Placeholder Management's rows: Save creates
 * this one row, "+" copies it below and the cross drops it locally. Saving every draft at once is the
 * floating bar's Save.
 * @param options translator, cap state and the handlers
 * @returns the row-action configs for one draft row
 */
export const buildDraftRowActions = ({ t, canAdd, onSave, onAddBelow, onRemove }: DraftRowActionsCtx): RowActionConfig[] => [
  {
    icon: 'bombiix-icon-save',
    title: t('COMMON_LABEL.SAVE'),
    className: 'bombiix-icon-container--green-hover',
    onClick: (row: TableRow) => onSave(row),
  },
  {
    icon: 'bombiix-icon-add',
    title: t('product-information:ADD_ROW_BELOW'),
    className: 'bombiix-icon-container--blue',
    disabled: !canAdd,
    onClick: (row: TableRow) => onAddBelow(row),
  },
  {
    icon: 'bombiix-icon-close',
    title: t('product-information:REMOVE_DRAFT_ROW'),
    className: 'bombiix-icon-container--red',
    onClick: (row: TableRow) => onRemove(row),
  },
];
